import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp, Flame, Smile, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_LABELS = { 1: 'Terrible', 2: 'Bad', 3: 'Okay', 4: 'Good', 5: 'Amazing' };
const MOOD_COLORS = { 1: '#FF6B6B', 2: '#FFC8DD', 3: '#FDF0D5', 4: '#B9FBC0', 5: '#A2D2FF' };
const MOOD_EMOJIS = { 1: '😢', 2: '😟', 3: '😐', 4: '😊', 5: '🤩' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="neo-card p-3 !rounded-xl text-sm" style={{ boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)' }}>
      <p className="font-bold font-['Fredoka'] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}{p.name === 'Focus' ? ' min' : ''}</strong>
        </p>
      ))}
    </div>
  );
};

function DailyView() {
  const [data, setData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/stats/daily`, { withCredentials: true }),
          axios.get(`${API}/stats/weekly-breakdown`, { withCredentials: true }),
        ]);
        setData(a.data);
        setBreakdown(b.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar…</div>;
  if (!data) return <div className="neo-card p-8 text-center">Inga data än</div>;

  const neglected = (breakdown?.weekly_tasks || []).filter((t) => t.completions_4w === 0).slice(0, 6);
  const leastDone = (breakdown?.weekly_tasks || []).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="daily-stats-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="neo-card p-4 text-center bg-[#A2D2FF]">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']" data-testid="daily-tasks">{data.tasks_completed}</p>
          <p className="text-xs font-semibold">Tasks idag</p>
        </div>
        <div className="neo-card p-4 text-center bg-[#B9FBC0]">
          <Clock className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']" data-testid="daily-focus">{data.focus_minutes}</p>
          <p className="text-xs font-semibold">Fokusminuter</p>
        </div>
        <div className="neo-card p-4 text-center bg-[#FFC8DD]">
          <Smile className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']">{data.mood ? MOOD_EMOJIS[data.mood] : '—'}</p>
          <p className="text-xs font-semibold">Humör idag</p>
        </div>
        <div className="neo-card p-4 text-center bg-[#FDF0D5]">
          <Flame className="w-6 h-6 mx-auto mb-1 text-orange-500" />
          <p className="text-2xl font-bold font-['Fredoka']" data-testid="daily-cleaning">
            {data.cleaning_daily_done}<span className="opacity-50 text-base">/{data.cleaning_daily_total || '—'}</span>
          </p>
          <p className="text-xs font-semibold">Dagliga städ</p>
        </div>
      </div>

      {neglected.length > 0 && (
        <div className="neo-card p-5 bg-gradient-to-br from-[#FFE8E8] via-[#FFF3F9] to-[#FFF7E0]" data-testid="neglected-tasks">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-orange-600" /> Dessa har inte gjorts på 4 veckor
          </h3>
          <p className="text-xs opacity-70 mb-3">Inga dömar här — bara för att du ska kunna fånga upp det. 🌸</p>
          <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
            {neglected.map((t) => (
              <div key={t.id} className="neo-card p-3 bg-white flex items-center justify-between">
                <span className="font-bold text-sm">{t.title}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#FFD6A5]">0 ggr</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {leastDone.length > 0 && (
        <div className="neo-card p-5 bg-white" data-testid="least-done-tasks">
          <h3 className="font-['Fredoka'] font-bold text-lg mb-1">Mest åsidosatta veckouppgifter (senaste 4 veckorna)</h3>
          <DoodleSquiggle width={80} color="#FFC8DD" className="mb-3" />
          <div className="space-y-2">
            {leastDone.map((t) => (
              <div key={t.id} className="flex items-center gap-3">
                <span className="font-semibold flex-1 text-sm">{t.title}</span>
                <div className="flex-1 h-2.5 bg-black/10 rounded-full overflow-hidden border border-black">
                  <div className="h-full" style={{
                    width: `${Math.min(100, (t.completions_4w / 4) * 100)}%`,
                    background: t.completions_4w === 0 ? '#FF8C42' : '#B9FBC0',
                  }} />
                </div>
                <span className="text-xs font-bold w-12 text-right">{t.completions_4w} / 4v</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WeekView({ data }) {
  const chartData = data.days.map(d => ({
    name: d.label,
    Tasks: d.tasks_completed,
    Focus: d.focus_minutes,
    Mood: d.mood,
  }));

  const totalTasks = data.days.reduce((s, d) => s + d.tasks_completed, 0);
  const totalFocus = data.days.reduce((s, d) => s + d.focus_minutes, 0);
  const avgMood = data.days.filter(d => d.mood).reduce((s, d, _, a) => s + d.mood / a.length, 0);

  return (
    <div className="space-y-6" data-testid="weekly-stats-view">
      <div className="grid grid-cols-3 gap-3">
        <div className="neo-card p-4 text-center bg-[#A2D2FF]">
          <CheckCircle2 className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']">{totalTasks}</p>
          <p className="text-xs font-semibold">Tasks denna vecka</p>
        </div>
        <div className="neo-card p-4 text-center bg-[#B9FBC0]">
          <Clock className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']">{totalFocus}</p>
          <p className="text-xs font-semibold">Fokusminuter</p>
        </div>
        <div className="neo-card p-4 text-center bg-[#FFC8DD]">
          <Smile className="w-6 h-6 mx-auto mb-1" />
          <p className="text-2xl font-bold font-['Fredoka']">{avgMood ? avgMood.toFixed(1) : '-'}</p>
          <p className="text-xs font-semibold">Snitthumör</p>
        </div>
      </div>

      <div className="neo-card p-6">
        <h3 className="text-xl font-bold font-['Fredoka'] mb-1 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" /> Tasks klara
        </h3>
        <DoodleSquiggle width={80} color="#A2D2FF" className="mb-4" />
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" tick={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 13 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="Tasks" fill="#A2D2FF" radius={[8, 8, 0, 0]} stroke="#000" strokeWidth={1.5} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="neo-card p-6">
        <h3 className="text-xl font-bold font-['Fredoka'] mb-1 flex items-center gap-2">
          <Clock className="w-5 h-5" /> Fokusminuter
        </h3>
        <DoodleSquiggle width={80} color="#B9FBC0" className="mb-4" />
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" tick={{ fontFamily: 'Fredoka', fontWeight: 600, fontSize: 13 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="Focus" fill="#B9FBC0" radius={[8, 8, 0, 0]} stroke="#000" strokeWidth={1.5} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="neo-card p-6">
        <h3 className="text-xl font-bold font-['Fredoka'] mb-1 flex items-center gap-2">
          <Smile className="w-5 h-5" /> Humörtrend
        </h3>
        <DoodleSquiggle width={80} color="#FFC8DD" className="mb-4" />
        <div className="flex justify-between mb-2">
          {data.days.map((d, i) => (
            <div key={i} className="text-center flex-1">
              <p className="text-xs font-bold text-gray-500">{d.label}</p>
              <div
                className="w-10 h-10 mx-auto rounded-xl border-2 border-black flex items-center justify-center text-lg mt-1"
                style={{ backgroundColor: d.mood ? MOOD_COLORS[d.mood] : '#f0f0f0' }}
              >
                {d.mood ? MOOD_EMOJIS[d.mood] : '—'}
              </div>
              <p className="text-[10px] mt-0.5 text-gray-400">{d.mood ? MOOD_LABELS[d.mood] : ''}</p>
            </div>
          ))}
        </div>
      </div>

      {data.habit_streaks.length > 0 && (
        <div className="neo-card p-6">
          <h3 className="text-xl font-bold font-['Fredoka'] mb-1 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" /> Streaks
          </h3>
          <DoodleSquiggle width={80} color="#CDB4DB" className="mb-4" />
          <div className="space-y-3">
            {data.habit_streaks.map((h, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full border border-black" style={{ backgroundColor: h.color }} />
                <span className="font-semibold flex-1">{h.name}</span>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-2 py-1 bg-[#FDF0D5] rounded-full border border-black">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className="text-sm font-bold">{h.streak}d</span>
                  </div>
                  <span className="text-xs text-gray-400">{h.total} totalt</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WeeklyStats() {
  const [view, setView] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/stats/weekly`, { withCredentials: true });
      setData(data);
    } catch (e) {
      console.error('Failed to fetch weekly stats:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <div className="space-y-4" data-testid="weekly-stats">
      <div className="flex gap-2" data-testid="stats-view-toggle">
        <button
          onClick={() => setView('daily')}
          className={`neo-btn px-4 py-2 font-bold text-sm ${view === 'daily' ? 'bg-[#FFC8DD]' : 'bg-white'}`}
          data-testid="stats-view-daily"
        >
          📅 Idag
        </button>
        <button
          onClick={() => setView('weekly')}
          className={`neo-btn px-4 py-2 font-bold text-sm ${view === 'weekly' ? 'bg-[#A2D2FF]' : 'bg-white'}`}
          data-testid="stats-view-weekly"
        >
          📊 Denna vecka
        </button>
      </div>
      {view === 'daily' ? (
        <DailyView />
      ) : loading ? (
        <div className="neo-card p-8 text-center animate-pulse-soft">Laddar…</div>
      ) : data ? (
        <WeekView data={data} />
      ) : (
        <div className="neo-card p-8 text-center">Inga data än</div>
      )}
    </div>
  );
}

